import React, { useMemo, useState } from 'react';
import { fc, f0, planPaycheck, PLAN_DEFAULTS } from '../../utils';

const ICONS = {
  bills: '📋',
  spending: '🛒',
  ef_starter: '🛟',
  ef_full: '🛟',
  debt_extra: '💳',
  fund: '🎯',
  goal: '🎯',
  savings: '💵',
};

const SETTINGS = [
  { k: 'starterEF', label: 'Starter emergency fund', unit: '$', step: 100 },
  { k: 'efMonths', label: 'Full emergency fund (months of bills)', unit: 'mo', step: 1 },
  { k: 'highAprThreshold', label: 'High-interest debt starts at', unit: '% APR', step: 1 },
];

const dateKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fmtDate = (d) =>
  d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const stepId = (it) => `${it.key}:${it.targetId || it.label}`;

/**
 * PaycheckPlanCard — Autopilot's default plan for the next paycheck, shown as
 * a checklist. Users can tick steps off (worth XP) and change the defaults
 * the plan is built from (state.planSettings).
 */
export function PaycheckPlanCard({ state, updateState, setTab }) {
  const [adjusting, setAdjusting] = useState(false);
  const plan = useMemo(() => planPaycheck(state), [state]);
  const box = {
    marginBottom: 12,
    padding: '12px 14px',
    background: 'var(--surf)',
    border: '1px solid var(--bdr)',
    borderLeft: '3px solid var(--acc, #7C6FF7)',
    borderRadius: 12,
  };
  const title = {
    fontSize: 10,
    fontWeight: 700,
    color: 'var(--mut)',
    textTransform: 'uppercase',
    letterSpacing: '.5px',
  };

  if (!plan) {
    return (
      <div style={{ ...box, cursor: 'pointer' }} onClick={() => setTab?.('plan')}>
        <div style={title}>🧭 Autopilot</div>
        <div style={{ fontSize: 13, fontWeight: 600, marginTop: 6 }}>
          Add your paychecks under Money → Income and Autopilot will plan every paycheck for you.
        </div>
      </div>
    );
  }

  const key = dateKey(plan.payDate);
  const done = (state.planDone || {})[key] || [];
  const toggle = (id) => {
    const next = done.includes(id) ? done.filter((x) => x !== id) : [...done, id];
    // keep only the most recent few pay periods
    const kept = Object.entries(state.planDone || {})
      .filter(([k]) => k !== key)
      .sort(([a], [b]) => (a < b ? 1 : -1))
      .slice(0, 5);
    updateState({ planDone: { ...Object.fromEntries(kept), [key]: next } });
  };

  const ps = state.planSettings || {};
  const setSetting = (k, v) => {
    const n = parseFloat(v);
    const next = { ...ps };
    if (Number.isFinite(n) && n >= 0) next[k] = n;
    else delete next[k];
    updateState({ planSettings: next });
  };
  const customized = Object.keys(ps).length > 0;
  const efUntracked = plan.items.some((i) => i.key.startsWith('ef_') && !i.targetId);
  const doneCount = plan.items.filter((i) => done.includes(stepId(i))).length;

  return (
    <div style={box}>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}
      >
        <div style={title}>🧭 This Paycheck · Autopilot</div>
        <button
          onClick={() => setAdjusting((a) => !a)}
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--mut)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          {adjusting ? 'Done' : '⚙️ Adjust'}
        </button>
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          margin: '6px 0 2px',
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 800 }}>{fmtDate(plan.payDate)}</div>
        <div style={{ fontSize: 18, fontWeight: 900, color: 'var(--grn)' }}>{fc(plan.amt)}</div>
      </div>
      <div style={{ fontSize: 10, color: 'var(--mut)', marginBottom: 10 }}>
        Covers you until {fmtDate(plan.nextPayDate)} · {doneCount}/{plan.items.length} done ·{' '}
        {customized ? 'using your settings' : 'default plan, change it any time'}
      </div>

      {adjusting && (
        <div
          style={{
            padding: '10px',
            background: 'var(--bg)',
            border: '1px solid var(--bdr)',
            borderRadius: 8,
            marginBottom: 10,
          }}
        >
          {SETTINGS.map((s) => (
            <label
              key={s.k}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
                fontSize: 12,
                marginBottom: 6,
              }}
            >
              <span>{s.label}</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input
                  type="number"
                  min="0"
                  step={s.step}
                  value={ps[s.k] ?? PLAN_DEFAULTS[s.k]}
                  onChange={(e) => setSetting(s.k, e.target.value)}
                  style={{ width: 80, fontSize: 12 }}
                />
                <span style={{ fontSize: 10, color: 'var(--mut)', width: 38 }}>{s.unit}</span>
              </span>
            </label>
          ))}
          <div style={{ fontSize: 10, color: 'var(--mut)', marginTop: 4 }}>
            Spending money follows your category budgets, or your budget style slider if none are
            set. Debt order follows your Snowball/Avalanche choice.
          </div>
          {customized && (
            <button
              onClick={() => updateState({ planSettings: {} })}
              style={{
                marginTop: 8,
                fontSize: 11,
                fontWeight: 700,
                background: 'none',
                border: '1px solid var(--bdr)',
                borderRadius: 6,
                padding: '4px 8px',
                cursor: 'pointer',
                color: 'var(--txt)',
              }}
            >
              Reset to defaults
            </button>
          )}
        </div>
      )}

      {plan.shortfall > 0 && (
        <div
          style={{
            padding: '8px 10px',
            borderRadius: 8,
            fontSize: 11,
            color: 'var(--red)',
            background: 'rgba(255,107,107,.08)',
            border: '1px solid rgba(255,107,107,.2)',
            marginBottom: 10,
          }}
        >
          ⚠️ Bills before your next payday come to {f0(plan.billsTotal)}, which is{' '}
          {fc(plan.shortfall)} more than this paycheck. Plan to cover the gap from savings or move a
          due date.
        </div>
      )}

      {plan.items.map((it) => {
        const id = stepId(it);
        const checked = done.includes(id);
        return (
          <div
            key={id}
            onClick={() => toggle(id)}
            style={{
              display: 'flex',
              gap: 8,
              padding: '7px 0',
              borderTop: '1px solid var(--bdr)',
              cursor: 'pointer',
              opacity: checked ? 0.55 : 1,
            }}
          >
            <div style={{ width: 18, fontSize: 14 }}>{checked ? '✅' : ICONS[it.key] || '•'}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    textDecoration: checked ? 'line-through' : 'none',
                  }}
                >
                  {it.label}
                </span>
                <span style={{ fontSize: 13, fontWeight: 800 }}>{fc(it.amt)}</span>
              </div>
              <div style={{ fontSize: 10, color: 'var(--mut)', marginTop: 2 }}>{it.reason}</div>
            </div>
          </div>
        );
      })}

      {efUntracked && (
        <div style={{ fontSize: 10, color: 'var(--mut)', marginTop: 8 }}>
          💡 Add a savings goal named "Emergency Fund" so Autopilot can track how full it is.
        </div>
      )}
      <div style={{ fontSize: 10, color: 'var(--mut)', marginTop: 8 }}>
        Tap a step when it's done. Each one is worth 5 XP.
      </div>
    </div>
  );
}
